import {AgentType} from "@/app/data/types";
import {ProviderType} from "@/app/api/integrations/connect/types";
import {ArtifactType} from "@/pb/api/devplan/types/artifacts";
import {DocumentType} from "@/pb/api/devplan/types/documents";
import {PlanSimulation_Target} from "@/pb/api/devplan/types/tasks";

import {getRepoResearchKey, getRepoGuidanceKey, planTargetKey} from "@/hooks/queries/stores/util";
import {GetRepoResearchRequest, GetRepoResearchGuidanceRequest} from "@/pb/api/devplan/services/web/company";

export const companyKeys = {
  all: ['companies'] as const,
  list: () => [...companyKeys.all, 'list'] as const,
  details: () => [...companyKeys.all, 'details'] as const,
  detail: (id: number) => [...companyKeys.details(), id] as const,
  settings: (id: number) => [...companyKeys.details(), id, "settings"] as const,
  releases: (id: number) => [...companyKeys.detail(id), "releases"] as const,
  tags: (id: number) => [...companyKeys.detail(id), "tags"] as const,
  secrets: (id: number) => [...companyKeys.detail(id), "secrets"] as const,
}

export const adminOnboardingKeys = {
  all: ["admin-onboarding"] as const,
  list: () => [...adminOnboardingKeys.all, "list"] as const,
  slackChannels: () => [...adminOnboardingKeys.all, "slack-channels"] as const,
}

export const integrationKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'integrations'] as const,
  details: (companyId: number) => [...integrationKeys.all(companyId), 'detail'] as const,
  detail: (companyId: number, provider: ProviderType) => [...integrationKeys.details(companyId), provider] as const,
  properties: (companyId: number) => [...integrationKeys.all(companyId), 'properties'] as const,
  property: (companyId: number, provider: ProviderType) => [...integrationKeys.properties(companyId), provider] as const,
  githubManageUrl: (companyId: number) => [...integrationKeys.all(companyId), 'github-manage-url'] as const,
  githubPersonalOAuthStatus: (companyId: number) => [...integrationKeys.all(companyId), 'github-personal-oauth-status'] as const,
  jiraForgeAppStatus: (companyId: number) => [...integrationKeys.all(companyId), 'jira-forge-app-status'] as const,
  creditRewards: (companyId: number) => [...integrationKeys.all(companyId), 'credit-rewards'] as const,
  granolaConnection: (companyId: number) => [...integrationKeys.all(companyId), 'granola-connection'] as const,
  managedConnections: (companyId: number) => [...integrationKeys.all(companyId), 'managed-connections'] as const,
  managedConnectionTools: (companyId: number, slug: string) => [...integrationKeys.managedConnections(companyId), slug, 'tools'] as const,
}

export const repoMetaKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'repo-metas'] as const,
}

export type HomeReportModeKey = 'personal' | 'workspace' | 'team';
export type HomeReportPreviewModeKey = 'personal' | 'workspace' | 'all';
export type HomeReportCadenceKey = 'daily' | 'weekly';

export const homeReportKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'home-report'] as const,
  settings: (companyId: number) => [...homeReportKeys.all(companyId), 'settings'] as const,
  userPrefs: (companyId: number) => [...homeReportKeys.all(companyId), 'user-prefs'] as const,
  defaultInstruction: (companyId: number, cadence: HomeReportCadenceKey = 'daily') => [...homeReportKeys.all(companyId), 'default-instruction', cadence] as const,
  detail: (companyId: number, reportId: string) => [...homeReportKeys.all(companyId), 'detail', reportId] as const,
  latest: (companyId: number, mode: HomeReportModeKey, teamId?: string) => [...homeReportKeys.all(companyId), 'latest', mode, ...(teamId ? [teamId] : [])] as const,
  preview: (companyId: number, mode: HomeReportPreviewModeKey, cadence: HomeReportCadenceKey = 'daily') => [...homeReportKeys.all(companyId), 'preview', mode, cadence] as const,
  history: (companyId: number, mode: HomeReportModeKey, teamId?: string) => [...homeReportKeys.all(companyId), 'history', mode, ...(teamId ? [teamId] : [])] as const,
  combinedHistory: (companyId: number) => [...homeReportKeys.all(companyId), 'history', 'all'] as const,
}

export const documentKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'documents'] as const,
  lists: (companyId: number) => [...documentKeys.all(companyId), 'list'] as const,
  forProject: (companyId: number, projectId: string) => [...documentKeys.lists(companyId), {projectId}] as const,
  byParent: (companyId: number, parentId: string, type?: DocumentType) => [...documentKeys.lists(companyId), {
    parentId,
    type
  }] as const,
  details: (companyId: number) => [...documentKeys.all(companyId), 'detail'] as const,
  detail: (companyId: number, id: string) => [...documentKeys.details(companyId), id] as const,
}

export type ProjectsFilter = 'withDocs' | 'withDocs-archived' | 'ideas' | 'ideas-archived';

export const projectKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'projects'] as const,
  lists: (companyId: number) => [...projectKeys.all(companyId), 'list'] as const,
  list: (companyId: number, filter: ProjectsFilter) => [...projectKeys.lists(companyId), {filter}] as const,
  details: (companyId: number) => [...projectKeys.all(companyId), 'detail'] as const,
  detail: (companyId: number, id: string) => [...projectKeys.details(companyId), id] as const,
}

export const projectStatusKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'projectStatus'] as const,
  forProject: (companyId: number, projectId: string) => [...projectStatusKeys.all(companyId), projectId] as const,
  summary: (companyId: number, projectId: string) => [...projectStatusKeys.forProject(companyId, projectId), 'summary'] as const,
  updates: (companyId: number, projectId: string) => [...projectStatusKeys.forProject(companyId, projectId), 'updates'] as const,
  summariesBatch: (companyId: number, idsKey: string) => [...projectStatusKeys.all(companyId), 'summariesBatch', idsKey] as const,
  activitiesBatch: (companyId: number, idsKey: string) => [...projectStatusKeys.all(companyId), 'activitiesBatch', idsKey] as const,
  analysisStateBatch: (companyId: number, idsKey: string) => [...projectStatusKeys.all(companyId), 'analysisStateBatch', idsKey] as const,
}

export const projectSignalsKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'projectSignals'] as const,
  forProject: (companyId: number, projectId: string) => [...projectSignalsKeys.all(companyId), projectId] as const,
  feed: (companyId: number, projectId: string) => [...projectSignalsKeys.forProject(companyId, projectId), 'feed'] as const,
}

export const agentStateKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'agentStates'],
  documents: (companyId: number, documentId: string) => [...agentStateKeys.all(companyId), "documents", documentId],
  documentTyped: (companyId: number, documentId: string, agentType: AgentType) =>
    [...agentStateKeys.documents(companyId, documentId), "agent", agentType],
}

export const planningKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'planning'],
  target: (companyId: number, target: PlanSimulation_Target) =>
    [...planningKeys.all(companyId), planTargetKey(target)],
  targets: (companyId: number, targets: PlanSimulation_Target[]) => {
    const targetsStr = targets.map(t => planTargetKey(t)).sort().join("|")
    return [...planningKeys.all(companyId), "targets", targetsStr]
  }
}

export const templateKeys = {
  all: ['templates'] as const,
}

export const pulseKeys = {
  all: ['pulse'] as const,
  enrollment: (companyId: number) => [...pulseKeys.all, 'enrollment', companyId],
  digests: (companyId: number) => [...pulseKeys.all, 'digests', 'all', companyId],
  feed: (companyId: number) => [...pulseKeys.all, 'feed', companyId],
}

export const signalsKeys = {
  all: (companyId: number) => ['signals', companyId] as const,
  list: (companyId: number) => [...signalsKeys.all(companyId), 'list'] as const,
}

export const discussionTopicsKeys = {
  all: (companyId: number) => ['discussionTopics', companyId] as const,
  byIds: (companyId: number, topicIds: readonly string[]) =>
    [...discussionTopicsKeys.all(companyId), 'byIds', [...topicIds].sort()] as const,
}

export const insightsKeys = {
  all: (companyId: number) => ['insights', companyId] as const,
  lists: (companyId: number) => [...insightsKeys.all(companyId), 'list'] as const,
  list: (companyId: number, roleIds: readonly string[] = [], includeArchived = false) =>
    [...insightsKeys.lists(companyId), [...roleIds].sort(), {includeArchived}] as const,
  updates: (companyId: number, insightId: string) =>
    [...insightsKeys.all(companyId), 'updates', insightId] as const,
  roleCatalog: (companyId: number) => [...insightsKeys.all(companyId), 'roleCatalog'] as const,
  roleCatalogPlaceholder: () => ['insights', 'roleCatalog', 'no-company'] as const,
}

export const changesReportKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'changesReports'] as const,
  list: (companyId: number) => [...changesReportKeys.all(companyId), 'list'] as const,
  detail: (companyId: number, reportId: string) => [...changesReportKeys.all(companyId), 'detail', reportId] as const,
}

export const trendsKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'trends'] as const,
  byTimeframe: (companyId: number, timeframe: string) => [...trendsKeys.all(companyId), timeframe] as const,
}

export const roadmapKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'roadmaps'],
}

// TODO: Fold allRepoResearch / allRepoGuidance queryKeys. We should be ok leaving out GitProvider
//
export const artifactKeys = {
  all: (companyId: number, type: ArtifactType) => [...companyKeys.detail(companyId), 'artifacts', 'type', type],
  lists: (companyId: number) => [...companyKeys.detail(companyId), 'artifacts', 'list'],
  list: (companyId: number, id: string) => [...artifactKeys.lists(companyId), id],
  allRepoResearch: (companyId: number, repoId: string) => [...artifactKeys.lists(companyId), 'repoResearch'],
  repoResearch: (companyId: number, req: GetRepoResearchRequest) =>
    [...artifactKeys.lists(companyId), 'repoResearch', getRepoResearchKey(req)],
  allRepoGuidance: (companyId: number, repoId: string) => [...artifactKeys.lists(companyId), 'repoGuidance'],
  repoGuidance: (companyId: number, req: GetRepoResearchGuidanceRequest) =>
    [...artifactKeys.lists(companyId), 'repoGuidance', getRepoGuidanceKey(req)],
}

export const companyResearchKeys = {
  reports: (companyId: number) => [...companyKeys.detail(companyId), "companyResearchReports"] as const,
}

export const userKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'users'],
  org: (orgId: string) => [...orgKeys.detail(orgId), 'users'],
  current: ['user', 'current'] as const,
  usage: () => [...userKeys.current, 'usage'] as const,
  limits: () => [...userKeys.usage(), 'limits'] as const,
  preferences: () => [...userKeys.current, 'preferences'] as const,
  plans: () => [...userKeys.usage(), 'plans'] as const,
  subscription: () => [...userKeys.usage(), 'subscription'] as const,
}

export const commentsKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'comments'],
  thread: (companyId: number, threadId: string) => [...commentsKeys.all(companyId), "thread", threadId],
  byDocument: (companyId: number, documentId: string) => [...commentsKeys.all(companyId), "document", documentId],
}

export const globalConfigKeys = {
  config: ['admin', 'globalConfig'] as const,
  publicConfig: ['user', 'publicConfig'] as const,
}

export const featureFlagsKeys = {
  flags: ['global', 'featureFlags'] as const,
}

export const summariesKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'summaries'],
  byKind: (companyId: number, kind: "repos" | "sites") => [...summariesKeys.all(companyId), kind],
}

export const contextConfigurationKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'context-configuration'] as const,
}

export const workspaceMemoryKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'workspace-memories'] as const,
  lists: (companyId: number) => [...workspaceMemoryKeys.all(companyId), 'list'] as const,
  list: (companyId: number, view: string | boolean) =>
    [...workspaceMemoryKeys.lists(companyId), {view}] as const,
  details: (companyId: number) => [...workspaceMemoryKeys.all(companyId), 'detail'] as const,
  detail: (companyId: number, memoryId: string) => [...workspaceMemoryKeys.details(companyId), memoryId] as const,
}

export const teamKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'teams'] as const,
  lists: (companyId: number) => [...teamKeys.all(companyId), 'list'] as const,
  list: (companyId: number, scope: "all" | "mine" = "all") => [...teamKeys.lists(companyId), scope] as const,
  details: (companyId: number) => [...teamKeys.all(companyId), 'detail'] as const,
  detail: (companyId: number, teamId: string) => [...teamKeys.details(companyId), teamId] as const,
}

export const goalKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'goals'] as const,
  lists: (companyId: number) => [...goalKeys.all(companyId), 'list'] as const,
  list: (companyId: number, showArchived = false) => [...goalKeys.lists(companyId), {showArchived}] as const,
  details: (companyId: number) => [...goalKeys.all(companyId), 'detail'] as const,
  detail: (companyId: number, goalId: string) => [...goalKeys.details(companyId), goalId] as const,
  status: (companyId: number, goalId: string) => [...goalKeys.detail(companyId, goalId), 'status'] as const,
  summary: (companyId: number, goalId: string) => [...goalKeys.status(companyId, goalId), 'summary'] as const,
  updates: (companyId: number, goalId: string) => [...goalKeys.status(companyId, goalId), 'updates'] as const,
  summariesBatch: (companyId: number, idsKey: string) => [...goalKeys.all(companyId), 'summariesBatch', idsKey] as const,
}

export const dashboardKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'dashboards'] as const,
  lists: (companyId: number) => [...dashboardKeys.all(companyId), 'list'] as const,
  list: (companyId: number, archived = false) => [...dashboardKeys.lists(companyId), {archived}] as const,
  details: (companyId: number) => [...dashboardKeys.all(companyId), 'detail'] as const,
  detail: (companyId: number, dashboardId: string) => [...dashboardKeys.details(companyId), dashboardId] as const,
  prompt: (companyId: number, dashboardId: string) => [...dashboardKeys.detail(companyId, dashboardId), 'prompt'] as const,
  versions: (companyId: number, dashboardId: string) => [...dashboardKeys.detail(companyId, dashboardId), 'versions'] as const,
}

// Query keys for groups
export const groupKeys = {
  all: (companyId: number) => ['groups', companyId] as const,
  lists: (companyId: number) => [...groupKeys.all(companyId), 'list'] as const,
  list: (companyId: number) => [...groupKeys.lists(companyId)] as const,
  details: (companyId: number) => [...groupKeys.all(companyId), 'detail'] as const,
  detail: (companyId: number, id: string) => [...groupKeys.details(companyId), id] as const,
};

export const worklogKeys = {
  all: (companyId: number) => ['worklog', companyId] as const,
  project: (companyId: number, projectId: string) => [...worklogKeys.all(companyId), 'project', projectId] as const,
}

export const activityKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'activities'] as const,
  forCompany: (companyId: number) => [...activityKeys.all(companyId), 'company'] as const,
  forProject: (companyId: number, projectId: string) => [...activityKeys.all(companyId), 'project', projectId] as const,
}

export const historyKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'history'] as const,
  heatmap: (companyId: number, cadence: string, startDate: string, endDate: string) =>
    [...historyKeys.all(companyId), 'heatmap', cadence, startDate, endDate] as const,
  cellDetails: (companyId: number, bucketDate: string, rowId: string, groupBy: string, cadence: string) =>
    [...historyKeys.all(companyId), 'cellDetails', bucketDate, rowId, groupBy, cadence] as const,
}

export const orgKeys = {
  all: ['orgs'] as const,
  details: () => [...orgKeys.all, 'detail'] as const,
  detail: (id: string) => [...orgKeys.details(), id] as const,
  companies: (id: string) => [...orgKeys.detail(id), 'companies'] as const,
  subscription: (id: string) => [...orgKeys.detail(id), 'subscription'] as const,
  creditPlan: (id: string) => [...orgKeys.detail(id), 'credit-plan'] as const,
  creditPacks: (id: string) => [...orgKeys.detail(id), 'credit-packs'] as const,
  trial: (id: string) => [...orgKeys.detail(id), 'trial'] as const,
  dailyCreditUsage: (id: string) => [...orgKeys.detail(id), 'daily-credit-usage'] as const,
  invoicePreview: (id: string) => [...orgKeys.detail(id), 'invoice-preview'] as const,
}

export const jobKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'jobs'] as const,
  list: (companyId: number) => [...jobKeys.all(companyId), 'list'] as const,
  details: (companyId: number) => [...jobKeys.all(companyId), 'detail'] as const,
  detail: (companyId: number, jobId: string) => [...jobKeys.details(companyId), jobId] as const,
}

export const taskJobRunsKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'taskJobRuns'] as const,
  lists: (companyId: number) => [...taskJobRunsKeys.all(companyId), 'list'] as const,
  list: (companyId: number, taskId: string) => [...taskJobRunsKeys.lists(companyId), taskId] as const,
}

export const setupJobRunsKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'setupJobRuns'] as const,
  list: (companyId: number) => [...setupJobRunsKeys.all(companyId), 'list'] as const,
}

export const attachmentKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'attachments'] as const,
  forProject: (companyId: number, projectId: string) =>
    [...attachmentKeys.all(companyId), {projectId}] as const,
  forCompany: (companyId: number) =>
    [...attachmentKeys.all(companyId), 'company'] as const,
  forIntegration: (companyId: number) =>
    [...attachmentKeys.all(companyId), 'integration'] as const,
}

export const catalogueKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'catalogue'] as const,
  list: (companyId: number) => [...catalogueKeys.all(companyId), 'list'] as const,
  check: (companyId: number) => [...catalogueKeys.all(companyId), 'check'] as const,
  featureUpdates: (companyId: number, featureId: string) => [...catalogueKeys.all(companyId), 'featureUpdates', featureId] as const,
}

export const pendingJobKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'pendingJobs'] as const,
}

export const linkPreviewKeys = {
  all: (companyId: number) => [...integrationKeys.all(companyId), 'link-previews'] as const,
  preview: (companyId: number, provider: string, identifier: string, url: string) =>
    [...linkPreviewKeys.all(companyId), provider, identifier, url] as const,
}

export const workspaceGraphKeys = {
  all: (companyId: number) => [...companyKeys.detail(companyId), 'workspaceGraph'] as const,
  subgraph: (companyId: number, rootType: string, rootId: string, maxDegree: number, nodeCap: number, edgeCap: number) =>
    [...workspaceGraphKeys.all(companyId), 'subgraph', rootType, rootId, maxDegree, nodeCap, edgeCap] as const,
}
